import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, CardBody, Input, Page, Select, useToast } from "@theg1group/ui";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.ts";
import { fmtAgo, fmtDate, severityTone } from "../lib/format.ts";
import type { AlertRow, PartnerRow, Rows } from "../lib/types.ts";

export function Alerts() {
	const qc = useQueryClient();
	const toast = useToast();
	const [unread, setUnread] = useState(true);
	const [kind, setKind] = useState("");
	const [partnerId, setPartnerId] = useState("");
	const [limit, setLimit] = useState("100");

	const partners = useQuery({
		queryKey: ["partners", "", ""],
		queryFn: () => api.get<Rows<PartnerRow>>("/api/partners?q=&status="),
	});
	const list = useQuery({
		queryKey: ["alerts", { unread, kind, partnerId, limit }],
		queryFn: () => {
			const p = new URLSearchParams();
			if (unread) p.set("unread", "1");
			if (kind) p.set("kind", kind);
			if (partnerId) p.set("partnerId", partnerId);
			if (limit) p.set("limit", limit);
			return api.get<Rows<AlertRow>>(`/api/alerts?${p.toString()}`);
		},
		refetchInterval: 30_000,
	});

	const invalidate = () => {
		qc.invalidateQueries({ queryKey: ["alerts"] });
		qc.invalidateQueries({ queryKey: ["stats"] });
	};
	const markRead = useMutation({
		mutationFn: (id: string) => api.post(`/api/alerts/${id}/read`),
		onSuccess: invalidate,
		onError: (e) => toast.addToast("error", String(e)),
	});
	const readAll = useMutation({
		mutationFn: () => api.post("/api/alerts/read-all"),
		onSuccess: () => {
			toast.addToast("success", "All alerts marked read");
			invalidate();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	const kinds = useMemo(() => {
		const s = new Set<string>();
		for (const a of list.data?.rows ?? []) s.add(a.kind);
		if (kind) s.add(kind);
		return [...s].sort();
	}, [list.data, kind]);

	const rows = list.data?.rows ?? [];

	return (
		<Page
			title="Alerts"
			description="Watch hits, sanctioned counterparties, budget stops and overlap findings."
			actions={
				<Button variant="secondary" loading={readAll.isPending} onClick={() => readAll.mutate()}>
					Mark all read
				</Button>
			}
		>
			<div className="mb-3 flex flex-wrap gap-2 items-center">
				<label className="flex items-center gap-2 text-sm text-content cursor-pointer">
					<input type="checkbox" checked={unread} onChange={(e) => setUnread(e.target.checked)} />
					Unread only
				</label>
				<div className="w-48">
					<Select
						value={kind}
						onChange={(e) => setKind(e.target.value)}
						options={[
							{ value: "", label: "All kinds" },
							...kinds.map((k) => ({ value: k, label: k })),
						]}
						size="sm"
					/>
				</div>
				<div className="w-56">
					<Select
						value={partnerId}
						onChange={(e) => setPartnerId(e.target.value)}
						options={[
							{ value: "", label: "All partners" },
							...(partners.data?.rows ?? []).map((p) => ({ value: p.id, label: p.name })),
						]}
						size="sm"
					/>
				</div>
				<div className="w-24">
					<Input
						type="number"
						min={1}
						max={1000}
						value={limit}
						onChange={(e) => setLimit(e.target.value)}
						size="sm"
						title="Limit"
					/>
				</div>
			</div>

			<Card>
				<CardBody>
					{list.isLoading && <div className="text-muted text-sm">Loading…</div>}
					{!list.isLoading && !rows.length && <div className="text-muted text-sm">No alerts.</div>}
					<div className="flex flex-col divide-y divide-line">
						{rows.map((a) => (
							<div
								key={a.id}
								className={`flex items-start gap-3 py-2.5 ${a.readAt ? "opacity-60" : ""}`}
							>
								<Badge variant={severityTone(a.severity)}>{a.severity}</Badge>
								<div className="min-w-0 flex-1">
									<div className="text-sm text-content font-medium">{a.title}</div>
									{a.body && (
										<div className="text-xs text-muted mt-0.5 whitespace-pre-wrap">{a.body}</div>
									)}
									<div className="text-[11px] text-muted mt-1 flex flex-wrap gap-x-2">
										<span>{a.kind}</span>
										<span title={fmtDate(a.createdAt)}>{fmtAgo(a.createdAt)}</span>
										{a.partnerId && (
											<Link
												to={`/partners/${a.partnerId}`}
												className="text-primary hover:underline"
											>
												{a.partnerName ?? "partner"} →
											</Link>
										)}
										{a.addressId && (
											<Link
												to={`/addresses/${a.addressId}`}
												className="text-primary hover:underline"
											>
												address →
											</Link>
										)}
										{a.slackSentAt && <span>· slack ✓</span>}
										{a.readAt && <span>· read {fmtAgo(a.readAt)}</span>}
									</div>
								</div>
								{!a.readAt && (
									<Button size="sm" variant="ghost" onClick={() => markRead.mutate(a.id)}>
										Mark read
									</Button>
								)}
							</div>
						))}
					</div>
				</CardBody>
			</Card>
		</Page>
	);
}
